import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Container, Row, Col, Form, Button, Alert, Table, Spinner, Modal } from 'react-bootstrap';
import { FaSave, FaLayerGroup, FaArrowLeft, FaExternalLinkAlt, FaUsers } from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DataApi from '../../api/DataApi';
import './css/LayoutRecordViewRoleBulkPage.css';

/**
 * Matrix of roles (rows) × record view (columns) with detail layout per cell.
 * Click a cell to assign or open the layout designer in Object Manager.
 *
 * @param {object} [props]
 * @param {boolean} [props.embedded] - When true, fixed object only (no object picker / back nav); for use inside a modal.
 * @param {string} [props.fixedObjectName] - Object API name when embedded.
 * @param {string|null} [props.highlightRecordviewId] - Scroll this record view column into view (UUID string).
 */
function LayoutRecordViewRoleBulkPage({
  embedded = false,
  fixedObjectName = '',
  highlightRecordviewId = null,
} = {}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [objects, setObjects] = useState([]);
  const [objectName, setObjectName] = useState('');
  const [loadingObjects, setLoadingObjects] = useState(true);
  const [loadingContext, setLoadingContext] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [detailLayouts, setDetailLayouts] = useState([]);
  const [recordviews, setRecordviews] = useState([]);
  const [roles, setRoles] = useState([]);
  const [assignments, setAssignments] = useState([]);

  const [cellModal, setCellModal] = useState(null);
  /** Column-wide assignment: same popup UI, applies to every role in that scope. */
  const [columnModal, setColumnModal] = useState(null);
  const [modalDetailLayoutId, setModalDetailLayoutId] = useState('');
  const [filterRoleId, setFilterRoleId] = useState('');
  const [filterLayoutId, setFilterLayoutId] = useState('');

  const lastUrlObjectRef = useRef('');

  useEffect(() => {
    if (embedded) {
      setLoadingObjects(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoadingObjects(true);
      setError('');
      try {
        const api = new DataApi('objectconfig');
        const res = await api.fetchAll();
        if (!cancelled && res?.data?.success) {
          const list = (res.data.data || []).filter((o) => !o.is_deleted);
          setObjects(list);
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || e.message || 'Failed to load objects');
      } finally {
        if (!cancelled) setLoadingObjects(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [embedded]);

  useEffect(() => {
    const obj = embedded
      ? (fixedObjectName || '').trim()
      : (searchParams.get('object') || searchParams.get('objectName') || '').trim();
    const objectChanged = obj !== lastUrlObjectRef.current;
    lastUrlObjectRef.current = obj;
    setObjectName(obj);
    if (objectChanged) {
      setCellModal(null);
      setColumnModal(null);
      setModalDetailLayoutId('');
      setFilterRoleId('');
      setFilterLayoutId('');
    }
  }, [embedded, fixedObjectName, searchParams]);

  const loadContext = useCallback(async (name) => {
    if (!name) {
      setDetailLayouts([]);
      setRecordviews([]);
      setRoles([]);
      setAssignments([]);
      return;
    }
    setLoadingContext(true);
    setError('');
    try {
      const api = new DataApi('objectconfig/layouts');
      const res = await api.get(`/bulk-role-assign/${encodeURIComponent(name)}`);
      if (res?.data?.success) {
        const d = res.data.data || {};
        setDetailLayouts(d.layouts?.detail || []);
        setRecordviews(d.recordviews || []);
        setRoles(d.roles || []);
        setAssignments(d.assignments || []);
      } else {
        setError(res?.data?.message || 'Failed to load context');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load context');
    } finally {
      setLoadingContext(false);
    }
  }, []);

  useEffect(() => {
    if (objectName) loadContext(objectName);
  }, [objectName, loadContext]);

  const detailAssignments = useMemo(
    () =>
      (assignments || []).filter(
        (row) => String(row.layout_type || '').toUpperCase() === 'DETAIL'
      ),
    [assignments]
  );

  const assignmentMap = useMemo(() => {
    const m = new Map();
    for (const row of detailAssignments) {
      if (row.recordview_id == null || String(row.recordview_id).trim() === '') continue;
      const key = `${row.role_id}::${String(row.recordview_id)}`;
      if (!m.has(key)) m.set(key, row);
    }
    return m;
  }, [detailAssignments]);

  const scopeColumns = useMemo(() => {
    const isDefaultRv = (rv) =>
      rv.is_standard === true || String(rv.name || '').toLowerCase() === 'default';
    const rvs = [...recordviews]
      .filter((r) => !r.is_deleted && r.is_active !== false)
      .sort((a, b) => {
        const ad = isDefaultRv(a);
        const bd = isDefaultRv(b);
        if (ad !== bd) return ad ? -1 : 1;
        return String(a.view_label || a.name || '').localeCompare(
          String(b.view_label || b.name || ''),
          undefined,
          { sensitivity: 'base' }
        );
      });
    return rvs.map((rv) => ({
      key: String(rv.id),
      label: rv.view_label || rv.name || rv.id,
      recordviewId: String(rv.id),
    }));
  }, [recordviews]);

  const roleRows = useMemo(() => {
    return [...roles]
      .filter((r) => r.is_active !== false)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
  }, [roles]);

  const filteredRoleRows = useMemo(() => {
    let list = roleRows;
    if (filterRoleId) {
      list = list.filter((r) => String(r.id) === String(filterRoleId));
    }
    if (filterLayoutId) {
      const want = String(filterLayoutId);
      list = list.filter((role) =>
        scopeColumns.some((col) => {
          const a = assignmentMap.get(`${role.id}::${col.key}`);
          return a?.layout_id != null && String(a.layout_id) === want;
        })
      );
    }
    return list;
  }, [roleRows, filterRoleId, filterLayoutId, scopeColumns, assignmentMap]);

  const pushUrlParams = (obj) => {
    if (embedded) return;
    const next = new URLSearchParams();
    if (obj) next.set('object', obj);
    setSearchParams(next, { replace: true });
  };

  const getConsensusLayoutIdForColumn = useCallback(
    (col, rolesList) => {
      if (!rolesList?.length) return '';
      let common = null;
      for (const role of rolesList) {
        const a = assignmentMap.get(`${role.id}::${col.key}`);
        const lid = a?.layout_id != null ? String(a.layout_id) : '';
        if (common === null) common = lid;
        else if (common !== lid) return '';
      }
      return common || '';
    },
    [assignmentMap]
  );

  const openCellModal = (role, col) => {
    setColumnModal(null);
    const row = assignmentMap.get(`${role.id}::${col.key}`);
    setCellModal({
      roleId: role.id,
      roleName: role.name || role.id,
      columnLabel: col.label,
      recordviewId: col.recordviewId,
    });
    setModalDetailLayoutId(row?.layout_id ? String(row.layout_id) : '');
  };

  const openColumnModal = (col) => {
    setCellModal(null);
    setColumnModal({ col });
    setModalDetailLayoutId(getConsensusLayoutIdForColumn(col, filteredRoleRows));
  };

  const closeAssignmentModal = () => {
    if (!saving) {
      setCellModal(null);
      setColumnModal(null);
      setModalDetailLayoutId('');
    }
  };

  const saveCellModal = async () => {
    if (!cellModal || !objectName) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const api = new DataApi('objectconfig/layouts');
      const body = {
        recordviewId: cellModal.recordviewId,
        roleIds: [cellModal.roleId],
        editLayoutId: null,
      };
      if (!modalDetailLayoutId) {
        body.clearDetailOnly = true;
      } else {
        body.detailLayoutId = modalDetailLayoutId;
      }
      const res = await api.put(`/bulk-role-assign/${encodeURIComponent(objectName)}`, body);
      if (res?.data?.success) {
        setSuccess(modalDetailLayoutId ? 'Assignment saved.' : 'Assignment removed.');
        await loadContext(objectName);
        closeAssignmentModal();
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(res?.data?.message || 'Save failed');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveColumnForAllRoles = async (col, detailLayoutId, clearOnly, roleIdsForBulk) => {
    const ids = roleIdsForBulk?.length ? roleIdsForBulk : roleRows.map((r) => r.id);
    if (!objectName || !ids.length) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const api = new DataApi('objectconfig/layouts');
      const body = {
        recordviewId: col.recordviewId,
        roleIds: ids,
        editLayoutId: null,
      };
      if (clearOnly || !detailLayoutId) {
        body.clearDetailOnly = true;
      } else {
        body.detailLayoutId = detailLayoutId;
      }
      const res = await api.put(`/bulk-role-assign/${encodeURIComponent(objectName)}`, body);
      if (res?.data?.success) {
        setSuccess(
          clearOnly || !detailLayoutId
            ? `Removed detail layout for all roles — ${col.label}.`
            : `Applied layout to all roles — ${col.label}.`
        );
        await loadContext(objectName);
        closeAssignmentModal();
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(res?.data?.message || 'Save failed');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveColumnModal = async () => {
    if (!columnModal?.col || !objectName) return;
    await saveColumnForAllRoles(
      columnModal.col,
      modalDetailLayoutId,
      !modalDetailLayoutId,
      filteredRoleRows.map((r) => r.id)
    );
  };

  const openLayoutDesigner = (layoutId) => {
    if (!layoutId || !objectName) return;
    const q = new URLSearchParams({
      view: 'layouts',
      object: objectName,
      layoutId: String(layoutId),
    });
    navigate(`/setup/objectmanager?${q.toString()}`);
  };

  const getCellAssignment = (roleId, col) => assignmentMap.get(`${roleId}::${col.key}`);

  return (
    <Container
      fluid
      className={`layout-rv-role-bulk-page ${embedded ? 'py-2 layout-rv-role-bulk-page--embedded' : 'py-4'}`}
    >
      {!embedded && (
        <div className="d-flex align-items-center gap-2 mb-3">
          <Button variant="outline-secondary" size="sm" onClick={() => navigate('/setup/objectmanager')}>
            <FaArrowLeft className="me-1" />
            Object Manager
          </Button>
        </div>
      )}
      <h4 className="d-flex align-items-center gap-2 mb-1">
        <FaLayerGroup className="text-primary" />
        Layout &amp; record view access
      </h4>
      {embedded && objectName ? (
        <p className="text-muted small mb-2">
          Object: <strong className="text-body">{objectName}</strong>
        </p>
      ) : null}
      <p className="text-muted small mb-4">
        Each cell is one role and one record view. The users icon next to each column heading opens the{' '}
        <strong>same assignment dialog</strong> for <strong>all roles</strong> in that column. Click a cell for one role
        only. Use <strong>Design layout</strong> in the dialog to open the page layout editor. Filters narrow which roles
        appear; column actions apply only to <strong>visible</strong> roles.
      </p>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {!embedded &&
        (loadingObjects ? (
          <div className="text-muted py-4">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading objects…
          </div>
        ) : (
          <Row className="g-3 mb-4">
            <Col md={6} lg={4}>
              <Form.Group>
                <Form.Label>Object</Form.Label>
                <Form.Select
                  value={objectName}
                  onChange={(e) => {
                    const v = e.target.value;
                    setObjectName(v);
                    pushUrlParams(v);
                  }}
                >
                  <option value="">— Select —</option>
                  {objects.map((o) => (
                    <option key={o.object_name} value={o.object_name}>
                      {o.object_label || o.object_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        ))}

      {objectName && loadingContext && (
        <div className="text-muted py-3">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading roles, record views, and assignments…
        </div>
      )}

      {objectName && !loadingContext && (
        <>
          <Row className="g-3 mb-3">
            <Col md={6} lg={4}>
              <Form.Group>
                <Form.Label className="small text-muted mb-1">Filter by role</Form.Label>
                <Form.Select
                  size="sm"
                  value={filterRoleId}
                  onChange={(e) => setFilterRoleId(e.target.value)}
                  disabled={!roleRows.length}
                >
                  <option value="">All roles</option>
                  {roleRows.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} lg={4}>
              <Form.Group>
                <Form.Label className="small text-muted mb-1">Filter by layout</Form.Label>
                <Form.Select
                  size="sm"
                  value={filterLayoutId}
                  onChange={(e) => setFilterLayoutId(e.target.value)}
                  disabled={!detailLayouts.length}
                >
                  <option value="">All layouts</option>
                  {detailLayouts.map((l) => (
                    <option key={l.layout_id} value={l.layout_id}>
                      {l.layout_name}
                      {l.is_standard ? ' (standard)' : ''}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            {(filterRoleId || filterLayoutId) && (
              <Col xs={12} className="d-flex align-items-end">
                <Button
                  variant="link"
                  size="sm"
                  className="p-0"
                  onClick={() => {
                    setFilterRoleId('');
                    setFilterLayoutId('');
                  }}
                >
                  Clear filters
                </Button>
              </Col>
            )}
          </Row>

          <div className="layout-rv-matrix-wrap border rounded bg-white">
          {roleRows.length === 0 ? (
            <p className="text-muted small p-3 mb-0">No active roles.</p>
          ) : filteredRoleRows.length === 0 ? (
            <p className="text-muted small p-3 mb-0">
              No roles match the current filters. <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => { setFilterRoleId(''); setFilterLayoutId(''); }}>Clear filters</Button>
            </p>
          ) : scopeColumns.length === 0 ? (
            <p className="text-muted small p-3 mb-0">
              No active record views for this object. Create record views (including the standard <strong>Default</strong>{' '}
              view) in Object Manager, then assign detail layouts per record view here.
            </p>
          ) : (
            <Table responsive bordered hover size="sm" className="mb-0 layout-rv-matrix-table">
              <thead className="table-light">
                <tr>
                  <th className="layout-rv-matrix-role-col">Role</th>
                  {scopeColumns.map((col) => (
                    <th
                      key={col.key}
                      id={`layout-rv-col-${col.key}`}
                      className={`text-center small align-middle ${
                        highlightRecordviewId && String(highlightRecordviewId) === String(col.key)
                          ? 'table-info'
                          : ''
                      }`}
                    >
                      <div className="d-flex flex-row align-items-center justify-content-center gap-2 flex-wrap">
                        <span className="fw-semibold">{col.label}</span>
                        <Button
                          type="button"
                          variant="outline-secondary"
                          size="sm"
                          className="layout-rv-matrix-column-all-btn p-1 rounded-circle flex-shrink-0"
                          title={`Set detail layout for all roles — ${col.label}`}
                          aria-label={`Set detail layout for all roles in ${col.label}`}
                          disabled={saving || !filteredRoleRows.length}
                          onClick={() => openColumnModal(col)}
                        >
                          <FaUsers />
                        </Button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRoleRows.map((role) => (
                  <tr key={role.id}>
                    <td className="layout-rv-matrix-role-col fw-medium small">{role.name}</td>
                    {scopeColumns.map((col) => {
                      const a = getCellAssignment(role.id, col);
                      const label = a?.layout_name || '— Assign —';
                      return (
                        <td key={col.key} className="text-center align-middle small">
                          <button
                            type="button"
                            className={`btn btn-link btn-sm p-0 layout-rv-matrix-cell-btn ${a ? '' : 'text-muted'}`}
                            onClick={() => openCellModal(role, col)}
                          >
                            {label}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
        </>
      )}

      <Modal show={!!cellModal || !!columnModal} onHide={closeAssignmentModal} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">Detail layout assignment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cellModal && (
            <>
              <p className="small mb-2">
                <strong>Role:</strong> {cellModal.roleName}
              </p>
              <p className="small mb-3">
                <strong>Record view:</strong> {cellModal.columnLabel}
              </p>
            </>
          )}
          {columnModal && (
            <>
              <p className="small mb-2 d-flex align-items-center gap-2">
                <FaUsers className="text-primary" />
                <span>
                  <strong>Roles in view</strong> ({filteredRoleRows.length}
                  {filterRoleId || filterLayoutId ? ` of ${roleRows.length}` : ''}) — same layout for each listed role in
                  this column.
                </span>
              </p>
              <p className="small mb-3">
                <strong>Record view:</strong> {columnModal.col.label}
              </p>
            </>
          )}
          {(cellModal || columnModal) && (
            <>
              <Form.Group>
                <Form.Label>Detail page layout</Form.Label>
                <Form.Select
                  value={modalDetailLayoutId}
                  onChange={(e) => setModalDetailLayoutId(e.target.value)}
                  disabled={saving}
                >
                  <option value="">— No assignment —</option>
                  {detailLayouts.map((l) => (
                    <option key={l.layout_id} value={l.layout_id}>
                      {l.layout_name} {l.is_standard ? '(standard)' : ''}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              {modalDetailLayoutId ? (
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="mt-3"
                  onClick={() => openLayoutDesigner(modalDetailLayoutId)}
                  disabled={saving}
                >
                  <FaExternalLinkAlt className="me-1" />
                  Design layout
                </Button>
              ) : null}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={closeAssignmentModal} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              if (cellModal) void saveCellModal();
              else void saveColumnModal();
            }}
            disabled={saving || (!cellModal && !columnModal)}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving…
              </>
            ) : (
              <>
                <FaSave className="me-1" />
                Save
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default LayoutRecordViewRoleBulkPage;
